// Слотът до склада на продукта при разнасяне (`warehouseAddon`):
// - складът е предложеният → малък белег откъде е дошъл („Склад на продукта" /
//   „Склад на станцията на рецептата") в подсказка;
// - операторът е сменил склада на ЕДИН друг → действие „Винаги от <склад>":
//   след потвърждение складът става своят склад на продукта за ресторанта
//   (PUT /inventory/product-warehouses/{id}). Явно действие, НЕ тихо
//   запомняне — случайна смяна (взех от бара заради недостиг в кухнята) не
//   остава завинаги. Прегледът се опреснява, работният лист — не.
// Стар BE без `defaultAllocations` → слотът е празен.
import { App, Tooltip } from "antd";
import { PushpinFilled, PushpinOutlined, ShopOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { ReportPostingProduct } from "../../../../api/inventory";
import { useSetProductWarehouse } from "../../../../hooks/useInventoryProductWarehouses";
import { useConfirm } from "../../components/confirm/useConfirm";
import { ROW_ICON_BUTTON } from "../../components/list/density";
import { warehouseHintOf } from "./reportPostingDefaults";

type Props = {
  product: ReportPostingProduct;
  warehouseIds: number[];
  warehouseNameOf: (warehouseId: number) => string;
};

export const ReportPostingWarehouseHint = ({ product, warehouseIds, warehouseNameOf }: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const confirm = useConfirm();
  const setMutation = useSetProductWarehouse();
  const hint = warehouseHintOf(product, warehouseIds);
  if (!hint) return null;

  if (hint.kind === "source") {
    const label =
      hint.source === "product"
        ? t("inventoryPage.postingWhSourceProduct")
        : t("inventoryPage.postingWhSourceStation");
    return (
      <Tooltip title={label}>
        <span
          role="img"
          aria-label={label}
          className="inline-flex shrink-0 items-center text-[12px] text-faint"
        >
          {hint.source === "product" ? <PushpinFilled /> : <ShopOutlined />}
        </span>
      </Tooltip>
    );
  }

  const warehouseName = warehouseNameOf(hint.warehouseId);
  const pin = () =>
    confirm({
      intent: "info",
      icon: <PushpinOutlined />,
      title: t("inventoryPage.postingAlwaysFromTitle", {
        product: product.productName,
        warehouse: warehouseName,
      }),
      explanation: t("inventoryPage.postingAlwaysFromBody", { warehouse: warehouseName }),
      note: { kind: "undo", text: t("inventoryPage.postingAlwaysFromUndo") },
      confirmLabel: t("inventoryPage.postingAlwaysFrom", { warehouse: warehouseName }),
      action: () =>
        setMutation.mutateAsync(
          { productId: product.productId, warehouseId: hint.warehouseId },
          {
            onSuccess: () => message.success(t("inventoryPage.productWarehouseSaved")),
            onError: (error: unknown) => {
              const err = error as { response?: { data?: { detail?: string } } };
              message.error(err.response?.data?.detail || t("inventoryPage.errSave"));
            },
          },
        ),
    });

  const label = t("inventoryPage.postingAlwaysFrom", { warehouse: warehouseName });
  return (
    <Tooltip title={label}>
      <button
        type="button"
        aria-label={label}
        onClick={pin}
        disabled={setMutation.isPending}
        className={`${ROW_ICON_BUTTON} inline-flex shrink-0 items-center justify-center rounded-md text-[13px] text-brand-deep-ink hover:bg-brand-tint disabled:opacity-50`}
      >
        <PushpinOutlined />
      </button>
    </Tooltip>
  );
};

export default ReportPostingWarehouseHint;
